import { create } from "zustand";
import { parseAgentCountryDirectionOptions } from "../../utilities/agentCountryCode";

const STORAGE_KEYS = {
  webrtcPhoneStartTime: "cti_webrtcPhoneStartTime",
  idleSessionStartedAt: "cti_idleSessionStartedAt",
};

const safeStorage = {
  getItem(key) {
    try {
      if (typeof window === "undefined" || !window?.localStorage) return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key, value) {
    try {
      if (typeof window === "undefined" || !window?.localStorage) return;
      window.localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  },
  removeItem(key) {
    try {
      if (typeof window === "undefined" || !window?.localStorage) return;
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

const initialWebrtcPhoneStartTime = safeStorage.getItem(STORAGE_KEYS.webrtcPhoneStartTime) || "";
const initialIdleSessionStartedAt =
  safeStorage.getItem(STORAGE_KEYS.idleSessionStartedAt) ||
  initialWebrtcPhoneStartTime ||
  "";

export const useCallStore = create((set, get) => ({
  //State
  callState: "idle",
  callerNumber: null,
  callerContact: null,
  callerContactReceivedAt: null,
  session: null,
  // Outgoing (webrtc dialer) helpers
  isPlacingCall: false,
  outgoingCallStatus: "",
  incomingReceivedAt: null,
  sipHeaders: null,
  sipCampaignId: null,
  sipCallGroupId: null,
  sipLeadData: null,
  isAnswering: false,
  isOnBreak: false,

  webrtcPhoneStatus:"",
  webrtcPhoneStartTime: initialWebrtcPhoneStartTime,

  idleSessionStartedAt: initialIdleSessionStartedAt,
  idleStatsSentForIncomingAt: null,

  callCreatedBy: null,
  direction: null,
  activeCallMeta: null,
  completedCallToPersist: null,
  activeCliId: null,
  activeCliNumber: null,
  activeCountryDirectionId: null,
  activeCallingCode: null,

  setAnswering: (value) => set({ isAnswering: value }),
  setOnBreak: (value) => set({ isOnBreak: Boolean(value) }),

  setCallState: (callState) => set({ callState }),
  setSession: (session) => set({ session }),
  setPlacingCall: (value) => set({ isPlacingCall: Boolean(value) }),
  setOutgoingCallStatus: (status) => set({ outgoingCallStatus: status ? String(status) : "" }),

  setWebrtcPhoneStatus: (status) =>
    set({
      webrtcPhoneStatus: status,
    }),

  setWebrtcPhoneStartTime: (time) =>
    set(() => {
      if (!time) {
        safeStorage.removeItem(STORAGE_KEYS.webrtcPhoneStartTime);
        safeStorage.removeItem(STORAGE_KEYS.idleSessionStartedAt);
        return {
          webrtcPhoneStartTime: "",
          idleSessionStartedAt: "",
        };
      }

      const storedIdle = safeStorage.getItem(STORAGE_KEYS.idleSessionStartedAt);
      const storedWebrtc = safeStorage.getItem(STORAGE_KEYS.webrtcPhoneStartTime);
      const effectiveTime = storedIdle || storedWebrtc || time;

      safeStorage.setItem(STORAGE_KEYS.webrtcPhoneStartTime, effectiveTime);
      safeStorage.setItem(STORAGE_KEYS.idleSessionStartedAt, effectiveTime);

      return {
        webrtcPhoneStartTime: effectiveTime,
        idleSessionStartedAt: effectiveTime,
      };
    }),

  setIdleSessionStartedAt: (time) =>
    set(() => {
      if (!time) {
        safeStorage.removeItem(STORAGE_KEYS.idleSessionStartedAt);
        return { idleSessionStartedAt: "" };
      }

      safeStorage.setItem(STORAGE_KEYS.idleSessionStartedAt, time);
      return { idleSessionStartedAt: time };
    }),

  markIdleStatsSentForIncomingAt: (receivedAt) =>
    set({
      idleStatsSentForIncomingAt: receivedAt,
    }),

  setCallCreatedBy: (userId) =>
    set({
      callCreatedBy: userId,
    }),

  setActiveCountryDirection: (direction, agentId) =>
    set(() => {
      if (!direction?.id) {
        if (agentId) {
          safeStorage.removeItem(`agent_active_country_${agentId}`);
        }
        return { activeCountryDirectionId: null, activeCallingCode: null };
      }
      const payload = {
        id: String(direction.id),
        callingCode: String(direction.callingCode || "").replace(/^\+/, ""),
      };
      if (agentId) {
        safeStorage.setItem(
          `agent_active_country_${agentId}`,
          JSON.stringify(payload)
        );
      }
      return {
        activeCountryDirectionId: payload.id,
        activeCallingCode: payload.callingCode,
      };
    }),

  loadActiveCountryForAgent: (agentId, agentCountryCodeDirections = []) =>
    set(() => {
      const options = parseAgentCountryDirectionOptions(agentCountryCodeDirections);
      if (!agentId || !options.length) {
        return { activeCountryDirectionId: null, activeCallingCode: null };
      }

      let stored = null;
      try {
        const raw = safeStorage.getItem(`agent_active_country_${agentId}`);
        if (raw) stored = JSON.parse(raw);
      } catch {
        stored = null;
      }

      const pick =
        stored && options.some((o) => o.id === String(stored.id))
          ? options.find((o) => o.id === String(stored.id))
          : options[0];

      const payload = {
        id: pick.id,
        callingCode: pick.callingCode,
      };
      safeStorage.setItem(
        `agent_active_country_${agentId}`,
        JSON.stringify(payload)
      );
      return {
        activeCountryDirectionId: payload.id,
        activeCallingCode: payload.callingCode,
      };
    }),

  setActiveCli: (cli, agentId) =>
    set(() => {
      if (!cli?.id) {
        if (agentId) {
          safeStorage.removeItem(`agent_active_cli_${agentId}`);
        }
        return { activeCliId: null, activeCliNumber: null };
      }
      const payload = {
        id: Number(cli.id),
        actual_number: String(cli.actual_number || ""),
      };
      if (agentId) {
        safeStorage.setItem(
          `agent_active_cli_${agentId}`,
          JSON.stringify(payload)
        );
      }
      return {
        activeCliId: payload.id,
        activeCliNumber: payload.actual_number,
      };
    }),

  loadActiveCliForAgent: (agentId, assignedClis = []) =>
    set(() => {
      if (!agentId || !Array.isArray(assignedClis) || !assignedClis.length) {
        return { activeCliId: null, activeCliNumber: null };
      }

      let stored = null;
      try {
        const raw = safeStorage.getItem(`agent_active_cli_${agentId}`);
        if (raw) stored = JSON.parse(raw);
      } catch {
        stored = null;
      }

      const defaultCli =
        assignedClis.find((c) => c.is_default) || assignedClis[0];
      const pick =
        stored &&
        assignedClis.some((c) => Number(c.id) === Number(stored.id))
          ? assignedClis.find((c) => Number(c.id) === Number(stored.id))
          : defaultCli;

      if (!pick) {
        return { activeCliId: null, activeCliNumber: null };
      }

      const payload = {
        id: Number(pick.id),
        actual_number: String(pick.actual_number || ""),
      };
      safeStorage.setItem(
        `agent_active_cli_${agentId}`,
        JSON.stringify(payload)
      );
      return {
        activeCliId: payload.id,
        activeCliNumber: payload.actual_number,
      };
    }),

  startActiveCall: (meta) =>
    set((state) => {
      const resolvedDirection = meta?.direction || state.direction || null;
      return {
        direction: resolvedDirection,
        activeCallMeta: {
          ...(meta || {}),
          direction: resolvedDirection,
          start_time: meta?.start_time || new Date().toISOString(),
          created_by: meta?.created_by || get().callCreatedBy || null,
        },
      };
    }),

  updateActiveCallMeta: (partial = {}) =>
    set((state) => ({
      activeCallMeta: state.activeCallMeta
        ? { ...state.activeCallMeta, ...(partial || {}) }
        : state.activeCallMeta,
    })),

  markActiveCallAnswered: (meta = {}) =>
    set((state) => ({
      activeCallMeta: state.activeCallMeta
        ? {
            ...state.activeCallMeta,
            answer_status: meta.answer_status || "answered",
            bridge_time: meta.bridge_time || state.activeCallMeta.bridge_time || new Date().toISOString(),
          }
        : state.activeCallMeta,
    })),

  markActiveCallDeclinedByAgent: () =>
    set((state) => ({
      activeCallMeta: state.activeCallMeta
        ? {
            ...state.activeCallMeta,
            answer_status: "not_answered",
            hangup_cause: "declined_by_agent",
          }
        : state.activeCallMeta,
    })),

  completeActiveCall: (meta = {}) =>
    set((state) => {
      const endTime = meta.end_time || new Date().toISOString();
      const active = state.activeCallMeta || {};
      const from = active.start_time ? new Date(active.start_time) : null;
      const bridge = active.bridge_time ? new Date(active.bridge_time) : null;
      const to = endTime ? new Date(endTime) : null;

      const resolvedAnswerStatus = meta.answer_status || active.answer_status || "";
      const isAnswered =
        resolvedAnswerStatus === "answered" ||
        Boolean(active.bridge_time && bridge && !Number.isNaN(bridge.getTime()));

      const ringEnd = isAnswered ? bridge : to;
      const ringDuration =
        from && ringEnd && !Number.isNaN(from.getTime()) && !Number.isNaN(ringEnd.getTime())
          ? Math.max(0, Math.floor((ringEnd.getTime() - from.getTime()) / 1000))
          : 0;

      const talkDuration =
        isAnswered && bridge && to && !Number.isNaN(bridge.getTime()) && !Number.isNaN(to.getTime())
          ? Math.max(0, Math.floor((to.getTime() - bridge.getTime()) / 1000))
          : 0;

      const completed = {
        ...active,
        ...meta,
        end_time: endTime,
        answer_status: resolvedAnswerStatus || (isAnswered ? "answered" : "not_answered"),
        hangup_cause:
          meta.hangup_cause ||
          active.hangup_cause ||
          (isAnswered ? "hangup" : "missed"),
        bridge_ring_duration: meta.bridge_ring_duration ?? ringDuration,
        patch_duration: meta.patch_duration ?? talkDuration,
        billing_duration: meta.billing_duration ?? talkDuration,
      };

      return {
        completedCallToPersist: completed,
        activeCallMeta: null,
      };
    }),

  clearCompletedCallToPersist: () =>
    set({
      completedCallToPersist: null,
    }),

  setSipData: ({ sipHeaders, sipCampaignId, sipCallGroupId, sipLeadData } = {}) =>
    set({
      sipHeaders: sipHeaders ?? null,
      sipCampaignId: sipCampaignId ?? null,
      sipCallGroupId: sipCallGroupId ?? null,
      sipLeadData: sipLeadData ?? null,
    }),

  setCallerContact: (contact, receivedAt) =>
    set({
      callerContact: contact ?? null,
      callerContactReceivedAt: receivedAt || new Date().toISOString(),
    }),

  //Incoming Call
  setIncomingCall: ({ number, session, receivedAt }) =>
    set((state) => ({
      callState: "incoming",
      direction: "incoming",
      callerNumber: number,
      callerContact: null,
      callerContactReceivedAt: null,
      session,
      incomingReceivedAt: receivedAt || new Date().toISOString(),
      idleStatsSentForIncomingAt: null,
      sipHeaders: null,
      sipCampaignId: null,
      sipCallGroupId: null,
      sipLeadData: null,
      isPlacingCall: false,
      outgoingCallStatus: "",
      activeCallMeta: state.activeCallMeta
        ? { ...state.activeCallMeta, direction: "incoming" }
        : state.activeCallMeta,
    })),

  // Outgoing Call (Dialer)
  setOutgoingCall: ({ number, session, startedAt } = {}) =>
    set((state) => ({
      callState: "dialing",
      direction: "outgoing",
      callerNumber: number ?? state.callerNumber ?? null,
      callerContact: null,
      callerContactReceivedAt: null,
      session: session ?? state.session ?? null,
      incomingReceivedAt: null,
      idleStatsSentForIncomingAt: null,
      activeCallMeta: state.activeCallMeta
        ? { ...state.activeCallMeta, direction: "outgoing" }
        : state.activeCallMeta,
    })),

  //Call Connected
  setConnected: () =>
    set({
      callState: "oncall",
    }),

  //Hold
  setHold: (held) =>
    set({
      callState: held ? "hold" : "connected",
    }),

  // End Call
  endCall: (options = {}) =>
    set((state) => {
      const shouldResetIdle = options?.resetIdle !== false;
      const nextIdleStart = shouldResetIdle ? new Date().toISOString() : state.idleSessionStartedAt;

      if (shouldResetIdle) {
        safeStorage.setItem(STORAGE_KEYS.idleSessionStartedAt, nextIdleStart);
      }

      return {
        callState: "idle",
        direction: null,
        callerNumber: null,
        callerContact: null,
        callerContactReceivedAt: null,
        session: null,
        isPlacingCall: false,
        outgoingCallStatus: "",
        incomingReceivedAt: null,
        sipHeaders: null,
        sipCampaignId: null,
        sipCallGroupId: null,
        sipLeadData: null,
        idleSessionStartedAt: nextIdleStart,
        idleStatsSentForIncomingAt: null,
      };
    }),
}));
